import { useQuery } from "@tanstack/react-query";
import { formatISO } from "date-fns";
import { useTranslation } from "react-i18next";
import { DevErrorDisplay } from "../../../components/core/blocks/DevErrorDisplay";
import Loading from "../../../components/core/blocks/Loading";
import { useAppSelector } from "../../../redux/hooks";
import { GoalsConfigForm, type Goal } from "../GoalsConfigForm";
import { useOrgGoalsQueryOptions } from "../api/useOrgGoalsQueryOptions";
import { useSetOrgGoals } from "../api/useSetOrgGoals";

export function ConfigTab({ displayPeriod }: { displayPeriod: Date }) {
  const { t } = useTranslation(["goals"]);
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);

  const orgGoalQuery = useQuery({
    ...useOrgGoalsQueryOptions(
      formatISO(new Date(displayPeriod.getFullYear(), 0, 1), {
        representation: "date",
      }),
      formatISO(new Date(displayPeriod.getFullYear(), 11, 31), {
        representation: "date",
      }),
    ),
    select(data) {
      // Transform the data into a record of goals per period
      return data.reduce(
        (acc, goal) => {
          acc[goal.start_date] = {
            total_sales: goal.total_sales_goal,
            tacos: (goal.ad_spend_goal / goal.total_sales_goal) * 100,
            ad_spend: goal.ad_spend_goal,
          };
          return acc;
        },
        {} as Record<string, Goal>,
      );
    },
    refetchOnMount: true,
  });

  const setGoalsMutation = useSetOrgGoals();

  return (
    orgGoalQuery.isLoading || !orgGoalQuery.isFetchedAfterMount ? <Loading />
    : orgGoalQuery.isSuccess ?
      <GoalsConfigForm
        initialValues={orgGoalQuery.data}
        onSubmit={async (values) => {
          const payload = Object.entries(values)
            .filter(([, goal]) => !Number.isNaN(goal.total_sales) && !Number.isNaN(goal.ad_spend))
            .map(([start_date, goal]) => ({
              start_date,
              total_sales_goal: goal.total_sales,
              ad_spend_goal: goal.ad_spend,
              market_place: marketPlace,
            }));
          await setGoalsMutation.mutateAsync(payload);
        }}
        displayYear={displayPeriod.getFullYear()}
      />
    : <DevErrorDisplay
        userMessage={t(($) => $.goals["There was an error loading the goal configuration"])}
        error={orgGoalQuery.error}
      />
  );
}
